import { useTranslation } from 'react-i18next';
import { useDateTimeRange } from 'helpers/hooks/useDateTimeRange';
import { defaultProductColor } from 'theme/colors';
import { Box, Stack, Typography } from '@mui/material';
import { VisaLogo, PrismaLogo } from 'assets/svg';
import { PRODUCTS_COLORS, ProductName } from '@globalConstants';
import { Chip } from '@molecules';
import NubiLogoMark from './NubiLogoMark';
import styles from './styles';
import { CardColors, PrepaidCard as IPrepaidCard } from '../useProducts';

interface PrepaidCardProps {
  card: IPrepaidCard;
  product: ProductName | null;
  customColors?: CardColors;
  segment: string;
  categories: string;
}

/** Preapaid card component */
const PrepaidCard = ({ card, product, customColors, segment, categories }: PrepaidCardProps) => {
  const { t } = useTranslation();
  const isProvidedByPrisma = card.provider === 'PRISMA';
  const useDateTimeRangeConfig = useDateTimeRange(segment);
  const productColor = customColors || (product && PRODUCTS_COLORS[product]) || defaultProductColor;
  const viewLeyendTime =
    product === 'Almuerzo' &&
    useDateTimeRangeConfig &&
    categories.toLowerCase().includes('almuerzo');
  const nubiLogoColor = product === 'Corporativo' ? '#fff' : '#000';

  return (
    <Box width={328} sx={styles.container(productColor)}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Chip
          sx={styles.chip(productColor)}
          variant='outlined'
          label={t(`COMMON:CARD_STATUS:${card.status}`)}
        />
        <Stack direction='row' alignItems='baseline' gap={1}>
          <Box sx={[isProvidedByPrisma ? styles.textAlignCenter : styles.alignSelfEnd]}>
            {isProvidedByPrisma && <PrismaLogo color={productColor.contrastText} />}
            <Typography sx={styles.caption(productColor)}>{card.provider}</Typography>
          </Box>
          <Box sx={{ margin: 0, padding: 0, textAlign: 'end', zIndex: 1 }}>
            <VisaLogo color={productColor.contrastText} />
            <Typography sx={styles.caption(productColor)}>{card.kind}</Typography>
          </Box>
        </Stack>
      </Stack>
      {card.ending && (
        <Stack alignItems='flex-end'>
          <Typography variant='h1' color='primary' sx={styles.text(productColor)}>
            •••• {card.ending}
          </Typography>
        </Stack>
      )}
      <Stack direction='row' justifyContent='space-between' alignItems='flex-end'>
        <Box>
          <Typography
            component='p'
            variant='caption'
            color='primary'
            sx={styles.text(productColor)}
          >
            {t('COMMON:TYPE')}
          </Typography>
          <Chip
            variant='filled'
            label={t(`EMPLOYEES:DETAIL:CARD_TYPE:${card.card_type}`)}
            sx={styles.chip(productColor)}
          />
        </Box>
        {card.expiration && (
          <Box sx={styles.expirationDate}>
            <Typography component='p' variant='caption' sx={styles.text(productColor)}>
              {t('COMMON:EXPIRATION_DATE')}
            </Typography>
            <Typography variant='h4' sx={styles.text(productColor)}>
              {card.expiration}
            </Typography>
          </Box>
        )}
      </Stack>
      {viewLeyendTime && (
        <Chip sx={styles.chip(productColor)} variant='outlined' label={useDateTimeRangeConfig} />
      )}
      <Box sx={styles.logoIcon}>
        <NubiLogoMark width={140} height={140} color={nubiLogoColor} />
      </Box>
    </Box>
  );
};

export default PrepaidCard;
